import { useState, useEffect } from 'react';
import {
  Button,
  Checkbox,
  ConfigProvider,
  Form,
  Input,
  Modal,
  Result,
  Select,
  Switch,
  Tabs,
  Tag,
  message,
  theme as antdTheme,
} from 'antd';
import {
  ApartmentOutlined,
  ControlOutlined,
  DeleteOutlined,
  LockOutlined,
  PlusOutlined,
  RollbackOutlined,
  TeamOutlined,
  UnlockOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '@/stores/userStore';
import {
  cabinConfigCreate,
  cabinConfigDelete,
  cabinConfigGetPermissions,
  cabinConfigGetUserCardIds,
  cabinConfigListAll,
  cabinConfigListMembers,
  cabinConfigSetPermissions,
  cabinConfigToggle,
  cabinConfigUpdate,
  type CabinCard,
  type CabinMember,
  type CreateCabinCardPayload,
  type UpdateCabinCardPayload,
} from '@/services/cabinConfig';
import styles from './ControlCenter.module.css';

const CABINS = [
  { id: 'collaboration', name: '战术协调中心' },
  { id: 'strategy',     name: '武器研究学院' },
  { id: 'system',       name: '工程装备车间' },
  { id: 'holographic',  name: '信息监控中心' },
  { id: 'finance',      name: '资源规划中心' },
  { id: 'personal',     name: '知识索引仓库' },
  { id: 'lifesupport',  name: '生命维持中心' },
];

function ControlCenter() {
  const navigate = useNavigate();
  const isAdmin = useUserStore((s) => s.isAdmin);

  const [allCards, setAllCards] = useState<CabinCard[]>([]);
  const [selectedCabinId, setSelectedCabinId] = useState('collaboration');
  const [loading, setLoading] = useState(false);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addForm] = Form.useForm();

  const [editCard, setEditCard] = useState<CabinCard | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm] = Form.useForm();

  const [permCard, setPermCard] = useState<CabinCard | null>(null);
  const [permUserIds, setPermUserIds] = useState<string[]>([]);
  const [permLoading, setPermLoading] = useState(false);
  const [permSaving, setPermSaving] = useState(false);

  const [members, setMembers] = useState<CabinMember[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userCardIds, setUserCardIds] = useState<number[]>([]);
  const [userCardLoading, setUserCardLoading] = useState(false);
  const [userCardSaving, setUserCardSaving] = useState(false);

  const [selectedPermCardId, setSelectedPermCardId] = useState<number | null>(null);
  const [cardPermUserIds, setCardPermUserIds] = useState<string[]>([]);
  const [cardPermLoading, setCardPermLoading] = useState(false);
  const [cardPermSaving, setCardPermSaving] = useState(false);

  const load = () => {
    setLoading(true);
    cabinConfigListAll()
      .then(setAllCards)
      .catch(() => message.error('加载失败'))
      .finally(() => setLoading(false));
  };

  const loadMembers = () => {
    cabinConfigListMembers().then(setMembers).catch(() => {});
  };

  useEffect(() => {
    load();
    loadMembers();
  }, []);

  const cabinCards = allCards.filter((c) => c.cabinId === selectedCabinId);
  const selectedCabinName = CABINS.find((c) => c.id === selectedCabinId)?.name ?? '';

  const handleToggle = async (card: CabinCard, enabled: boolean) => {
    try {
      await cabinConfigToggle(card.id, enabled);
      setAllCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, enabled } : c)));
    } catch { /* 拦截器已提示 */ }
  };

  const handleDelete = (card: CabinCard) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定删除卡片「${card.name}」吗？同时会清除该卡片的所有权限配置。`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        await cabinConfigDelete(card.id);
        setAllCards((prev) => prev.filter((c) => c.id !== card.id));
        message.success('已删除');
      },
    });
  };

  const handleAdd = async () => {
    try {
      const values = await addForm.validateFields();
      setAddLoading(true);
      const payload: CreateCabinCardPayload = {
        cabinId: values.cabinId,
        name: values.name,
        desc: values.desc || undefined,
        url: values.url || undefined,
      };
      const created = await cabinConfigCreate(payload);
      setAllCards((prev) => [...prev, created]);
      setAddModalOpen(false);
      addForm.resetFields();
      setSelectedCabinId(created.cabinId);
      message.success('卡片已创建，默认未启用');
    } catch { /* 表单验证错误或网络错误 */ } finally {
      setAddLoading(false);
    }
  };

  const openEditModal = (card: CabinCard) => {
    setEditCard(card);
    editForm.setFieldsValue({
      cabinId: card.cabinId,
      name: card.name,
      desc: card.desc ?? '',
      url: card.url ?? '',
    });
  };

  const handleEdit = async () => {
    if (!editCard) return;

    try {
      const values = await editForm.validateFields();
      setEditLoading(true);
      const payload: UpdateCabinCardPayload = {
        cabinId: values.cabinId,
        name: values.name,
        desc: values.desc ?? '',
        url: values.url ?? '',
      };
      const updated = await cabinConfigUpdate(editCard.id, payload);
      setAllCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      setEditCard(null);
      editForm.resetFields();
      setSelectedCabinId(updated.cabinId);
      message.success('卡片已更新');
    } catch { /* 表单验证错误或网络错误 */ } finally {
      setEditLoading(false);
    }
  };

  const openPermModal = async (card: CabinCard) => {
    setPermCard(card);
    setPermLoading(true);
    try {
      const ids = await cabinConfigGetPermissions(card.id);
      setPermUserIds(ids);
    } finally {
      setPermLoading(false);
    }
  };

  const savePermissions = async () => {
    if (!permCard) return;
    setPermSaving(true);
    try {
      await cabinConfigSetPermissions(permCard.id, permUserIds);
      message.success('权限已保存');
      setPermCard(null);
    } finally {
      setPermSaving(false);
    }
  };

  const handleSelectUser = async (userId: string) => {
    setSelectedUserId(userId);
    setUserCardLoading(true);
    try {
      const ids = await cabinConfigGetUserCardIds(userId);
      setUserCardIds(ids);
    } finally {
      setUserCardLoading(false);
    }
  };

  const handleUserCardToggle = async (cardId: number, checked: boolean) => {
    if (!selectedUserId) return;
    const prev = userCardIds;
    const next = checked ? [...prev, cardId] : prev.filter((id) => id !== cardId);
    setUserCardIds(next);
    setUserCardSaving(true);
    try {
      const current = await cabinConfigGetPermissions(cardId);
      let updated: string[];
      if (checked) {
        updated = current.includes(selectedUserId) ? current : [...current, selectedUserId];
      } else {
        updated = current.filter((id) => id !== selectedUserId);
      }
      await cabinConfigSetPermissions(cardId, updated);
    } catch {
      setUserCardIds(prev);
    } finally {
      setUserCardSaving(false);
    }
  };

  const handleSelectAllCards = async (checked: boolean) => {
    if (!selectedUserId) return;
    const enabledCards = allCards.filter((c) => c.enabled);
    const prev = userCardIds;
    const next = checked ? enabledCards.map((c) => c.id) : [];
    setUserCardIds(next);
    setUserCardSaving(true);
    try {
      await Promise.all(
        enabledCards.map(async (card) => {
          const current = await cabinConfigGetPermissions(card.id);
          let updated: string[];
          if (checked) {
            updated = current.includes(selectedUserId) ? current : [...current, selectedUserId];
          } else {
            updated = current.filter((id) => id !== selectedUserId);
          }
          await cabinConfigSetPermissions(card.id, updated);
        }),
      );
    } catch {
      setUserCardIds(prev);
    } finally {
      setUserCardSaving(false);
    }
  };

  const handleSelectPermCard = async (card: CabinCard) => {
    setSelectedPermCardId(card.id);
    setCardPermLoading(true);
    try {
      const ids = await cabinConfigGetPermissions(card.id);
      setCardPermUserIds(ids);
    } finally {
      setCardPermLoading(false);
    }
  };

  const handleSaveCardPermissions = async () => {
    if (!selectedPermCardId) return;
    setCardPermSaving(true);
    try {
      await cabinConfigSetPermissions(selectedPermCardId, cardPermUserIds);
      if (selectedUserId) {
        const ids = await cabinConfigGetUserCardIds(selectedUserId);
        setUserCardIds(ids);
      }
      message.success('权限已保存');
    } finally {
      setCardPermSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <Result
        status="403"
        title="403"
        subTitle="仅产品管理员可访问星舰控制中心。"
        extra={<Button type="primary" onClick={() => navigate('/dashboard')}>返回驾驶舱</Button>}
      />
    );
  }

  const cardMgmtTab = (
    <div className={styles.layout}>
      <div className={styles.cabinList}>
        {CABINS.map((cabin) => (
          <div
            key={cabin.id}
            className={`${styles.cabinItem} ${selectedCabinId === cabin.id ? styles.active : ''}`}
            onClick={() => setSelectedCabinId(cabin.id)}
          >
            {cabin.name}
          </div>
        ))}
      </div>

      <div className={styles.cardArea}>
        <div className={styles.cardAreaHeader}>
          <span className={styles.cabinTitle}>{selectedCabinName}</span>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
            {cabinCards.length} 个卡片
          </span>
        </div>

        {loading ? null : cabinCards.length === 0 ? (
          <div className={styles.emptyArea}>
            <p>暂无卡片，点击右上角「新增卡片」</p>
          </div>
        ) : (
          cabinCards.map((card) => (
            <div key={card.id} className={styles.cardRow}>
              <span className={styles.cardName}>{card.name}</span>
              <span className={styles.cardDesc}>{card.desc ?? '—'}</span>
              <span className={styles.cardUrl}>{card.url ?? '—'}</span>
              <div className={styles.cardActions}>
                <Switch
                  checked={card.enabled}
                  onChange={(checked) => handleToggle(card, checked)}
                  checkedChildren="显示"
                  unCheckedChildren="隐藏"
                />
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => openEditModal(card)}
                  title="编辑卡片"
                />
                <Button
                  size="small"
                  icon={<LockOutlined />}
                  onClick={() => openPermModal(card)}
                  title="权限管理"
                />
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(card)}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const userPerspectiveTab = (
    <div className={styles.layout}>
      <div className={styles.userList}>
        {members.length === 0 ? (
          <div className={styles.emptyArea} style={{ height: 200 }}>
            <p>暂无用户</p>
          </div>
        ) : (
          members.map((m) => (
            <div
              key={m.id}
              className={`${styles.cabinItem} ${selectedUserId === m.id ? styles.active : ''}`}
              onClick={() => handleSelectUser(m.id)}
            >
              <div style={{ fontWeight: 600, fontSize: 13 }}>{m.realName || m.username}</div>
              <div style={{ fontSize: 11, opacity: 0.5, marginTop: 2 }}>{m.email}</div>
            </div>
          ))
        )}
      </div>

      <div className={styles.cardArea}>
        {!selectedUserId ? (
          <div className={styles.emptyArea}>
            <p>← 选择左侧用户查看其卡片权限</p>
          </div>
        ) : (
          <>
            <div className={styles.cardAreaHeader}>
              <span className={styles.cabinTitle}>
                {(m => m?.realName || m?.username)(members.find((m) => m.id === selectedUserId))} 的卡片权限
              </span>
              {userCardSaving && (
                <span style={{ fontSize: 12, color: 'rgba(0,255,255,0.6)' }}>保存中…</span>
              )}
            </div>
            <div style={{ marginBottom: 12, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
              勾选 = 该用户可见（无任何勾选的卡片对所有人开放）
            </div>
            {userCardLoading ? null : (() => {
              const enabledCards = allCards.filter((c) => c.enabled);
              const allChecked = enabledCards.length > 0 && enabledCards.every((c) => userCardIds.includes(c.id));
              const someChecked = enabledCards.some((c) => userCardIds.includes(c.id));
              return (
                <>
                  <div className={styles.userCardRow} style={{ borderBottom: '1px solid rgba(0,255,255,0.18)', marginBottom: 4 }}>
                    <Checkbox
                      checked={allChecked}
                      indeterminate={!allChecked && someChecked}
                      onChange={(e) => handleSelectAllCards(e.target.checked)}
                      disabled={userCardSaving}
                    />
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>全选</span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
                      {userCardIds.filter(id => enabledCards.some(c => c.id === id)).length} / {enabledCards.length}
                    </span>
                  </div>
                  {enabledCards.map((card) => {
                    const isChecked = userCardIds.includes(card.id);
                    const cabin = CABINS.find((cb) => cb.id === card.cabinId);
                    return (
                      <div key={card.id} className={styles.userCardRow}>
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) => handleUserCardToggle(card.id, e.target.checked)}
                          disabled={userCardSaving}
                        />
                        <span className={styles.cardName} style={{ width: 120 }}>{card.name}</span>
                        <Tag color="rgba(0,255,255,0.15)" style={{ color: 'rgba(0,255,255,0.7)', border: 'none', fontSize: 11 }}>
                          {cabin?.name ?? card.cabinId}
                        </Tag>
                        <span className={styles.cardDesc}>{card.desc ?? ''}</span>
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );

  const cardPerspectiveTab = (() => {
    const selectedCard = allCards.find((card) => card.id === selectedPermCardId) ?? null;
    const visibleCards = allCards.filter((card) => card.enabled);

    return (
      <div className={styles.layout}>
        <div className={styles.cabinList}>
          {visibleCards.length === 0 ? (
            <div className={styles.emptyArea} style={{ height: 200 }}>
              <p>暂无已显示卡片</p>
            </div>
          ) : (
            visibleCards.map((card) => {
              const cabin = CABINS.find((cb) => cb.id === card.cabinId);
              return (
                <div
                  key={card.id}
                  className={`${styles.cabinItem} ${selectedPermCardId === card.id ? styles.active : ''}`}
                  onClick={() => handleSelectPermCard(card)}
                >
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{card.name}</div>
                  <div style={{ fontSize: 11, opacity: 0.5, marginTop: 2 }}>{cabin?.name ?? card.cabinId}</div>
                </div>
              );
            })
          )}
        </div>

        <div className={styles.cardArea}>
          {!selectedCard ? (
            <div className={styles.emptyArea}>
              <p>← 选择左侧卡片配置可见用户</p>
            </div>
          ) : (
            <>
              <div className={styles.cardAreaHeader}>
                <span className={styles.cabinTitle}>{selectedCard.name} 的可见用户</span>
                <Button
                  type="primary"
                  size="small"
                  onClick={handleSaveCardPermissions}
                  loading={cardPermSaving}
                  disabled={cardPermLoading}
                >
                  保存
                </Button>
              </div>
              <div style={{ marginBottom: 12, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
                不选择任何用户时，此卡片对所有用户开放；选择后将变为白名单模式。
              </div>
              {cardPermLoading ? null : (
                <>
                  <Checkbox
                    checked={members.length > 0 && members.every((m) => cardPermUserIds.includes(m.id))}
                    indeterminate={
                      cardPermUserIds.length > 0 &&
                      !members.every((m) => cardPermUserIds.includes(m.id))
                    }
                    onChange={(e) => {
                      setCardPermUserIds(e.target.checked ? members.map((m) => m.id) : []);
                    }}
                    disabled={cardPermSaving || members.length === 0}
                    style={{ marginBottom: 12 }}
                  >
                    全选用户
                  </Checkbox>
                  <Checkbox.Group
                    value={cardPermUserIds}
                    onChange={(vals) => setCardPermUserIds(vals as string[])}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                    disabled={cardPermSaving}
                  >
                    {members.map((m) => (
                      <Checkbox key={m.id} value={m.id}>
                        {m.realName || m.username}
                        <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.5 }}>{m.email}</span>
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                  {members.length === 0 && (
                    <p style={{ opacity: 0.4, textAlign: 'center' }}>暂无用户数据</p>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  })();

  const userPermTab = (
    <Tabs
      defaultActiveKey="byUser"
      items={[
        {
          key: 'byUser',
          label: '按用户配置',
          children: userPerspectiveTab,
        },
        {
          key: 'byCard',
          label: '按卡片配置',
          children: cardPerspectiveTab,
        },
      ]}
    />
  );

  return (
    <ConfigProvider
      theme={{
        algorithm: antdTheme.darkAlgorithm,
        token: {
          colorPrimary: '#00ffff',
          colorInfo: '#00ffff',
          colorBgContainer: 'rgba(10, 22, 44, 0.85)',
          colorBgElevated: 'rgba(10, 22, 44, 0.95)',
          colorBorder: 'rgba(0, 255, 255, 0.18)',
          colorBorderSecondary: 'rgba(0, 255, 255, 0.12)',
          fontFamily: "'PingFang SC', 'Microsoft YaHei', sans-serif",
          borderRadius: 2,
        },
      }}
    >
      <div className={styles.page}>
        <div className={styles.gridBackground} />
        <span className={`${styles.hudCorner} ${styles.tl}`} />
        <span className={`${styles.hudCorner} ${styles.tr}`} />
        <span className={`${styles.hudCorner} ${styles.bl}`} />
        <span className={`${styles.hudCorner} ${styles.br}`} />

        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleGroup}>
              <span className={styles.crumb}>AdenCockpit / CONTROL CENTER</span>
              <span className={styles.title}>
                <ControlOutlined className={styles.titleIcon} />
                星舰控制中心
              </span>
              <span className={styles.subtitle}>STARSHIP · CONTROL · CENTER</span>
            </div>
            <div className={styles.headerActions}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  addForm.setFieldValue('cabinId', selectedCabinId);
                  setAddModalOpen(true);
                }}
              >
                新增卡片
              </Button>
              <Button icon={<RollbackOutlined />} onClick={() => navigate('/dashboard')}>
                返回驾驶舱
              </Button>
            </div>
          </div>

          <Tabs
            defaultActiveKey="cards"
            items={[
              {
                key: 'cards',
                label: <span><ApartmentOutlined /> 卡片管理</span>,
                children: cardMgmtTab,
              },
              {
                key: 'users',
                label: <span><TeamOutlined /> 用户权限</span>,
                children: userPermTab,
              },
            ]}
          />
        </div>
      </div>

      <Modal
        title="新增卡片"
        open={addModalOpen}
        onOk={handleAdd}
        onCancel={() => { setAddModalOpen(false); addForm.resetFields(); }}
        okText="创建"
        cancelText="取消"
        confirmLoading={addLoading}
      >
        <Form form={addForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="cabinId" label="所属频道" rules={[{ required: true }]}>
            <Select options={CABINS.map((c) => ({ value: c.id, label: c.name }))} />
          </Form.Item>
          <Form.Item name="name" label="卡片名称" rules={[{ required: true, message: '请输入卡片名称' }]}>
            <Input placeholder="如 AdenGlue" maxLength={128} />
          </Form.Item>
          <Form.Item name="desc" label="副标题">
            <Input placeholder="如 伊敦协同工作平台" maxLength={256} />
          </Form.Item>
          <Form.Item name="url" label="跳转链接">
            <Input placeholder="如 https://glue.starship-aden.com" maxLength={512} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`编辑卡片 — ${editCard?.name ?? ''}`}
        open={!!editCard}
        onOk={handleEdit}
        onCancel={() => { setEditCard(null); editForm.resetFields(); }}
        okText="保存"
        cancelText="取消"
        confirmLoading={editLoading}
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="cabinId" label="所属频道" rules={[{ required: true }]}>
            <Select options={CABINS.map((c) => ({ value: c.id, label: c.name }))} />
          </Form.Item>
          <Form.Item name="name" label="卡片名称" rules={[{ required: true, message: '请输入卡片名称' }]}>
            <Input placeholder="如 AdenGlue" maxLength={128} />
          </Form.Item>
          <Form.Item name="desc" label="副标题">
            <Input placeholder="如 伊敦协同工作平台" maxLength={256} />
          </Form.Item>
          <Form.Item name="url" label="跳转链接">
            <Input placeholder="如 https://glue.starship-aden.com" maxLength={512} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`权限管理 — ${permCard?.name ?? ''}`}
        open={!!permCard}
        onOk={savePermissions}
        onCancel={() => setPermCard(null)}
        okText="保存"
        cancelText="取消"
        confirmLoading={permSaving}
        width={480}
      >
        {permLoading ? (
          <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: 24 }}>加载中…</p>
        ) : (
          <>
            <div style={{ marginBottom: 12, fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
              {permUserIds.length === 0
                ? <><UnlockOutlined /> 当前所有用户均可见此卡片。选择用户后将变为白名单模式。</>
                : <><LockOutlined /> 白名单模式：仅以下 {permUserIds.length} 名用户可见。清空所有选择可恢复公开。</>
              }
            </div>
            <Checkbox.Group
              value={permUserIds}
              onChange={(vals) => setPermUserIds(vals as string[])}
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              {members.map((m) => (
                <Checkbox key={m.id} value={m.id}>
                  {m.realName || m.username}
                  <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.5 }}>{m.email}</span>
                </Checkbox>
              ))}
            </Checkbox.Group>
            {members.length === 0 && (
              <p style={{ opacity: 0.4, textAlign: 'center' }}>暂无用户数据</p>
            )}
          </>
        )}
      </Modal>
    </ConfigProvider>
  );
}

export default ControlCenter;
